// ============================================
// RÉFÉRENTIEL — rapport de matching culturel (fit-culturel)
// ============================================
// Module PUR : pas de `ctx`, pas de lecture base, unit-testable.
//
// Restitution du fit-culturel calquée sur le modèle de rapport métier :
//   §1 indicateur global      → moyenne des r avec chaque référent, et son verdict
//   §2 lecture par les pôles  → convergences / frictions, au niveau ITEM
//   §3 détail par dimension   → agrégation par `categorie`, position moyenne
//   §4 pistes d'entretien     → phrases dérivées des §2/§3, par règles
//
// L'indicateur global est la MOYENNE DES CORRÉLATIONS entre le candidat et
// chaque référent, et ce même chiffre classe les candidats de la campagne
// (`comparerQsort`). Il se lit directement — le hasard vaut 0 — et se dit en
// mots : Proche, Proche à la limite, Pas de tendance nette, Éloigné à la
// limite, Éloigné (arbitrage du 30/10/2026). « Pas de tendance nette » est une
// absence de signal, pas un demi-fit.

import type { StatDimension } from "./agregation";
import {
  fiabilite,
  niveauxForcesQsort,
  vecteurEntrepriseForce,
} from "./agregation";
import type { VecteurQsort } from "./qsort";
import {
  NB_ITEMS_FIT_CULTUREL,
  PYRAMIDE_FIT_CULTUREL,
  scoresDeLaPyramide,
} from "./qsort";
import type { DimensionReferentiel } from "./validation";

/**
 * Vecteur de l'entreprise : les scores de la pyramide RECLASSÉE, restreints aux
 * items que l'agrégat renseigne.
 *
 * Les lectures item par item (§2, §3) lisent ce vecteur : le référentiel a la
 * même structure que la passation du candidat (§5.1), donc chaque item se
 * vérifie sur la pyramide affichée. L'indicateur global (§1), lui, part de la
 * moyenne continue (`correlationMoyenneReferents`), qui garde le désaccord entre
 * référents que le reclassement efface.
 *
 * `niveauxForces` vient de la projection ; recalculé si absent (projections
 * d'avant le reclassement).
 */
function vecteurEntreprise(args: {
  dimensions: DimensionReferentiel[];
  agregat: Record<string, StatDimension>;
  pyramide: number[];
  niveauxForces?: Record<string, number>;
}): VecteurQsort {
  const niveaux =
    args.niveauxForces ??
    niveauxForcesQsort({
      dimensions: args.dimensions,
      agregat: args.agregat,
      pyramide: args.pyramide,
    });
  return vecteurEntrepriseForce(niveaux, args.pyramide.length);
}

// --------------------------------------------
// §1 — Corrélation et significativité
// --------------------------------------------

export type Correlation = {
  /** Coefficient de corrélation, dans [-1, 1]. */
  r: number;
  /** p bilatéral. */
  p: number;
  /** Nombre d'items appariés (les deux vecteurs renseignés). */
  n: number;
};

/** Au-delà de ce seuil, r n'est pas distinguable du bruit → pas de tendance
 *  nette. */
export const SEUIL_SIGNIFICATIVITE = 0.05;

/** À partir de ce |r|, le verdict est net ; en deçà (mais significatif), il est
 *  « à la limite ». */
export const SEUIL_VERDICT_NET = 0.5;

/** Log-Gamma (Lanczos, 6 coefficients) — précision ~1e-10 sur les réels positifs. */
function lnGamma(x: number): number {
  const cof = [
    76.18009172947146, -86.50532032941677, 24.01409824083091,
    -1.231739572450155, 0.1208650973866179e-2, -0.5395239384953e-5,
  ];
  let y = x;
  let tmp = x + 5.5;
  tmp -= (x + 0.5) * Math.log(tmp);
  let ser = 1.000000000190015;
  for (let j = 0; j < 6; j++) ser += cof[j]! / ++y;
  return -tmp + Math.log((2.5066282746310005 * ser) / x);
}

/** Fraction continue de Lentz pour la bêta incomplète (Numerical Recipes §6.4). */
function betacf(a: number, b: number, x: number): number {
  const MAXIT = 300;
  const EPS = 3e-14;
  const FPMIN = 1e-300;
  const qab = a + b;
  const qap = a + 1;
  const qam = a - 1;
  let c = 1;
  let d = 1 - (qab * x) / qap;
  if (Math.abs(d) < FPMIN) d = FPMIN;
  d = 1 / d;
  let h = d;
  for (let m = 1; m <= MAXIT; m++) {
    const m2 = 2 * m;
    let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < FPMIN) d = FPMIN;
    c = 1 + aa / c;
    if (Math.abs(c) < FPMIN) c = FPMIN;
    d = 1 / d;
    h *= d * c;
    aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < FPMIN) d = FPMIN;
    c = 1 + aa / c;
    if (Math.abs(c) < FPMIN) c = FPMIN;
    d = 1 / d;
    const del = d * c;
    h *= del;
    if (Math.abs(del - 1) < EPS) break;
  }
  return h;
}

/** Bêta incomplète régularisée I_x(a, b). */
function betai(a: number, b: number, x: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const bt = Math.exp(
    lnGamma(a + b) -
      lnGamma(a) -
      lnGamma(b) +
      a * Math.log(x) +
      b * Math.log(1 - x),
  );
  return x < (a + 1) / (a + b + 2)
    ? (bt * betacf(a, b, x)) / a
    : 1 - (bt * betacf(b, a, 1 - x)) / b;
}

/**
 * p bilatéral d'un t de Student à `df` degrés de liberté.
 *
 * `P(|T| > t) = I_{df/(df+t²)}(df/2, 1/2)` — forme exacte, pas une approximation
 * normale : à n = 23 items (df = 21) l'écart à la normale reste sensible, et il
 * l'est franchement sur les campagnes à 15 items (df = 13).
 */
export function pBilateralStudent(t: number, df: number): number {
  if (df <= 0) return 1;
  if (!Number.isFinite(t)) return 0;
  return betai(df / 2, 0.5, df / (df + t * t));
}

/** p bilatéral d'un r de Pearson sur `n` items. */
function pDeCorrelation(r: number, n: number): number {
  const df = n - 2;
  // |r| = 1 → t infini, p = 0. Traité à part pour éviter le 1/0.
  return Math.abs(r) >= 1
    ? 0
    : pBilateralStudent(Math.abs(r) * Math.sqrt(df / (1 - r * r)), df);
}

/**
 * Plus petit |r| significatif au seuil de 5 % sur `n` items : 0,41 à 23 items,
 * 0,51 à 15. C'est la frontière de « Pas de tendance nette ». Recherche par
 * dichotomie sur `pDeCorrelation`, décroissant en |r|.
 */
export function rCritique(n: number): number {
  if (n < 3) return 1;
  let bas = 0;
  let haut = 1;
  for (let i = 0; i < 60; i++) {
    const milieu = (bas + haut) / 2;
    if (pDeCorrelation(milieu, n) > SEUIL_SIGNIFICATIVITE) bas = milieu;
    else haut = milieu;
  }
  return haut;
}

/**
 * r de Pearson entre deux vecteurs, sur les items communs aux deux.
 *
 * Sur des SCORES DE NIVEAU, c'est aussi le Spearman corrigé des ex æquo : sous
 * distribution forcée, le score de niveau *est* le rang moyen du paquet d'items
 * placés à ce niveau.
 *
 * `null` si moins de 3 items appariés, ou si un des deux vecteurs est constant
 * (r indéfini : division par un écart-type nul).
 */
export function correlationQsort(
  vCandidat: VecteurQsort,
  vEntreprise: VecteurQsort,
): Correlation | null {
  const codes = Object.keys(vCandidat).filter(
    (code) => vEntreprise[code] !== undefined,
  );
  const n = codes.length;
  if (n < 3) return null;

  const xs = codes.map((code) => vCandidat[code]!);
  const ys = codes.map((code) => vEntreprise[code]!);
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;

  let sxy = 0;
  let sxx = 0;
  let syy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i]! - mx;
    const dy = ys[i]! - my;
    sxy += dx * dy;
    sxx += dx * dx;
    syy += dy * dy;
  }
  if (sxx === 0 || syy === 0) return null;

  const r = Math.max(-1, Math.min(1, sxy / Math.sqrt(sxx * syy)));
  return { r, p: pDeCorrelation(r, n), n };
}

/**
 * Les conditions de l'identité de `correlationMoyenneReferents`, vérifiables sur
 * l'agrégat :
 * - le candidat a classé TOUS les items de l'agrégat, et ses scores sont
 *   exactement ceux de la pyramide (distribution forcée complète) ;
 * - chaque item a été classé par le même nombre de référents, et les deux
 *   premiers moments de l'agrégat sont ceux de classements forcés : la somme
 *   des moyennes est celle de la pyramide, et la somme des moyennes des carrés
 *   (σ² + moyenne², écart-type de population) celle de ses carrés. C'est
 *   exactement ce que l'identité exige de chaque référent — même moyenne, même
 *   dispersion que la pyramide —, vérifié ici en moyenne sur les référents.
 * L'agrégation n'admet plus que des classements forcés complets
 * (`estClassementForce`) ; ce garde protège les projections calculées avant.
 * Une contribution ancienne incomplète ou hors distribution forcée, ou une
 * passation partielle, sort du domaine de l'identité : « Non calculable »
 * plutôt qu'un chiffre faux.
 */
function identiteApplicable(
  vCandidat: VecteurQsort,
  agregat: Record<string, StatDimension>,
  pyramide: number[],
): boolean {
  const codes = Object.keys(agregat);
  if (codes.some((code) => vCandidat[code] === undefined)) return false;

  const croissant = (a: number, b: number) => a - b;
  const attendus = scoresDeLaPyramide(pyramide).sort(croissant);
  const candidat = codes.map((code) => vCandidat[code]!).sort(croissant);
  if (
    attendus.length !== candidat.length ||
    attendus.some((score, i) => score !== candidat[i])
  ) {
    return false;
  }

  const n = agregat[codes[0]!]!.n;
  if (codes.some((code) => agregat[code]!.n !== n)) return false;
  const somme = (valeurs: number[]) => valeurs.reduce((t, v) => t + v, 0);
  const premierMoment = somme(codes.map((code) => agregat[code]!.moyenne));
  const secondMoment = somme(
    codes.map(
      (code) => agregat[code]!.ecartType ** 2 + agregat[code]!.moyenne ** 2,
    ),
  );
  return (
    Math.abs(premierMoment - somme(attendus)) < 1e-6 &&
    Math.abs(secondMoment - somme(attendus.map((s) => s * s))) < 1e-6
  );
}

/**
 * INDICATEUR GLOBAL : moyenne des corrélations entre le classement du candidat
 * et celui de CHAQUE référent (arbitrage du 30/10/2026).
 *
 * Calculée depuis l'agrégat, sans relire les contributions. Sous distribution
 * forcée, tous les classements — candidat comme référents — ont la même moyenne
 * m et la même dispersion, celles de la pyramide. La corrélation avec le
 * référent k vaut donc Σ(cᵢ − m)(xₖᵢ − m) / Σ(cᵢ − m)², et leur moyenne
 * Σ(cᵢ − m)(x̄ᵢ − m) / Σ(cᵢ − m)², où x̄ᵢ est la moyenne des référents sur
 * l'item i, soit `agregat[code].moyenne`. Autrement dit : r avec la moyenne des
 * référents, multiplié par σ(x̄) / σ(pyramide). L'identité est exacte sous ses
 * conditions (`identiteApplicable`) ; `tests/unit/referentielRapport.test.ts`
 * la vérifie contre le calcul référent par référent.
 *
 * Ce que ça change par rapport à r sur la pyramide reclassée : le désaccord
 * entre référents compte. Plus ils divergent, plus σ(x̄) baisse et la moyenne
 * avec lui — un candidat qui pense comme un référent de plus obtient à peu près
 * l'accord moyen des référents entre eux. Deux référents exactement opposés
 * donnent 0. La valeur ne dépend pas, en revanche, du nombre de référents. Le
 * classement des candidats d'une campagne est le même qu'avec r sur la moyenne
 * des référents.
 *
 * `p` est celui d'un r de Pearson de même valeur sur n items. La moyenne est
 * toujours plus proche de 0 que r avec la moyenne des référents : ce p est donc
 * toujours au moins égal au p exact, jamais plus optimiste. Un verdict
 * « Proche » ou « Éloigné » reste significatif au seuil de 5 %.
 *
 * `null` (non calculable) sous 3 items, ou hors des conditions de l'identité.
 */
export function correlationMoyenneReferents(
  vCandidat: VecteurQsort,
  agregat: Record<string, StatDimension>,
  pyramide: number[],
): Correlation | null {
  const codes = Object.keys(agregat);
  const n = codes.length;
  if (n < 3 || !identiteApplicable(vCandidat, agregat, pyramide)) return null;

  const c = codes.map((code) => vCandidat[code]!);
  const x = codes.map((code) => agregat[code]!.moyenne);
  const mc = c.reduce((a, b) => a + b, 0) / n;
  const mx = x.reduce((a, b) => a + b, 0) / n;
  let sxy = 0;
  let scc = 0;
  for (let i = 0; i < n; i++) {
    sxy += (c[i]! - mc) * (x[i]! - mx);
    scc += (c[i]! - mc) ** 2;
  }
  // Pyramide à un seul niveau : aucun classement n'a de dispersion.
  if (scc === 0) return null;

  const r = Math.max(-1, Math.min(1, sxy / scc));
  return { r, p: pDeCorrelation(r, n), n };
}

/** Sens du verdict. `neutre` = pas de tendance nette, ou non calculable. */
export type SensVerdict = "proche" | "neutre" | "eloigne";

export type LibelleVerdict =
  | "Proche"
  | "Proche, à la limite"
  | "Pas de tendance nette"
  | "Éloigné, à la limite"
  | "Éloigné"
  | "Non calculable";

export type IndicateurGlobal = {
  correlation: Correlation | null;
  sens: SensVerdict;
  /** Significatif, mais sous `SEUIL_VERDICT_NET` en valeur absolue. Toujours
   *  `false` en `neutre`, où il n'y a rien à graduer. */
  aLaLimite: boolean;
  /** Le verdict en mots, LE MÊME sur tous les écrans et dans les documents. */
  libelle: LibelleVerdict;
  interpretation: string;
};

const LIBELLES_SENS: Record<
  Exclude<SensVerdict, "neutre">,
  { net: LibelleVerdict; limite: LibelleVerdict }
> = {
  proche: { net: "Proche", limite: "Proche, à la limite" },
  eloigne: { net: "Éloigné", limite: "Éloigné, à la limite" },
};

const INTERPRETATIONS: Record<LibelleVerdict, string> = {
  Proche:
    "Proche : le candidat valorise globalement ce que les référents jugent " +
    "nécessaire pour réussir dans le poste et l'organisation. À confirmer " +
    "valeur par valeur ci-dessous : un indicateur global peut masquer des " +
    "frictions ponctuelles.",
  "Proche, à la limite":
    "Proche, à la limite : la tendance va dans le sens d'un accord avec les " +
    "référents, mais elle dépasse de peu le seuil où elle se distingue du " +
    "hasard. À confirmer valeur par valeur ci-dessous, puis en entretien.",
  "Pas de tendance nette":
    "Pas de tendance nette : les priorités du candidat ne sont ni nettement " +
    "proches ni nettement éloignées de celles des référents. C'est une absence " +
    "de signal, pas un fit moyen : le PC Fit ne doit pas peser dans la décision.",
  "Éloigné, à la limite":
    "Éloigné, à la limite : la tendance va dans le sens d'un écart de priorités " +
    "avec les référents, mais elle dépasse de peu le seuil où elle se distingue " +
    "du hasard. À explorer en entretien avant toute conclusion.",
  Éloigné:
    "Éloigné : le candidat valorise globalement ce que les référents placent en " +
    "bas de leur classement, et inversement. À explorer en entretien avant " +
    "toute conclusion.",
  "Non calculable":
    "Indicateur global non calculable : passation du candidat ou contributions " +
    "des référents incomplètes (ou trop peu d'items communs).",
};

/**
 * Verdict en mots (arbitrage du 30/10/2026). Le chiffre reste visible dans le
 * détail du rapport, mais c'est le MOT qui se lit en premier.
 *
 *   p > .05                        → Pas de tendance nette
 *   significatif, |r| < 0,50       → Proche / Éloigné, à la limite
 *   significatif, |r| ≥ 0,50       → Proche / Éloigné
 *
 * À 23 items, « à la limite » couvre 0,41 à 0,49 en valeur absolue (cf.
 * `rCritique`). À 15 items, le seuil de signification (0,51) dépasse 0,50 : les
 * anciennes campagnes n'ont que trois verdicts.
 *
 * « Pas de tendance nette » ne se lit JAMAIS comme un fit moyen : c'est une
 * absence de signal, et `aLaLimite` y vaut `false` — l'UI ne peut pas
 * l'afficher comme un palier bas.
 */
export function interpreterCorrelation(
  correlation: Correlation | null,
): IndicateurGlobal {
  if (correlation === null) {
    return {
      correlation,
      sens: "neutre",
      aLaLimite: false,
      libelle: "Non calculable",
      interpretation: INTERPRETATIONS["Non calculable"],
    };
  }
  if (correlation.p > SEUIL_SIGNIFICATIVITE) {
    return {
      correlation,
      sens: "neutre",
      aLaLimite: false,
      libelle: "Pas de tendance nette",
      interpretation: INTERPRETATIONS["Pas de tendance nette"],
    };
  }

  const sens = correlation.r > 0 ? "proche" : "eloigne";
  const aLaLimite = Math.abs(correlation.r) < SEUIL_VERDICT_NET;
  const libelle = aLaLimite
    ? LIBELLES_SENS[sens].limite
    : LIBELLES_SENS[sens].net;
  return {
    correlation,
    sens,
    aLaLimite,
    libelle,
    interpretation: INTERPRETATIONS[libelle],
  };
}

// --------------------------------------------
// §2 — Lecture par les extrêmes
// --------------------------------------------

export type Pole = "haut" | "bas";

export type ItemPole = {
  code: string;
  label: string;
  categorie?: string;
  scoreCandidat: number;
  scoreEntreprise: number;
  /** Dispersion inter-référents sur cet item — un pôle peu consensuel est fragile. */
  dispersionEntreprise: number;
};

export type Convergence = ItemPole & {
  /** `haut` = valorisé des deux côtés. `bas` = écarté des deux côtés. */
  pole: Pole;
};

export type Friction = ItemPole & {
  /** Pôle du CANDIDAT ; l'entreprise est au pôle opposé par construction. */
  poleCandidat: Pole;
};

export type LectureExtremes = {
  convergences: Convergence[];
  frictions: Friction[];
  /** Nombre d'items retenus par pôle (capacité du niveau extrême de la pyramide). */
  taillePole: { haut: number; bas: number };
};

/**
 * Les `k` codes de plus forte (ou plus faible) valeur. Ex æquo tranchés par code
 * pour que deux rendus du même rapport donnent la même liste.
 */
function pole(vecteur: VecteurQsort, k: number, sens: Pole): Set<string> {
  const codes = Object.keys(vecteur).sort((a, b) => {
    const delta =
      sens === "haut" ? vecteur[b]! - vecteur[a]! : vecteur[a]! - vecteur[b]!;
    return delta !== 0 ? delta : a.localeCompare(b);
  });
  return new Set(codes.slice(0, k));
}

/**
 * Convergences et frictions lues aux PÔLES du tri — les positions les plus
 * fiables psychométriquement, à exploiter avant l'indicateur global.
 *
 * Un pôle contient autant d'items que le niveau extrême de la pyramide en
 * accueille (`pyramide[0]` en haut, le dernier niveau en bas). Les deux
 * pyramides ayant la même structure, un pôle est EXACTEMENT le contenu du niveau
 * extrême des deux côtés — plus d'ex æquo à trancher à la frontière, et
 * l'intersection se lit directement sur les deux écrans.
 *
 * Écart au modèle métier : les convergences incluent le pôle BAS
 * (`pole: "bas"`), pas seulement le haut. Deux parties qui écartent le même item
 * convergent aussi, et l'information est gratuite ici — l'UI distingue les deux
 * par le libellé.
 */
export function lectureExtremes(args: {
  dimensions: DimensionReferentiel[];
  vecteurCandidat: VecteurQsort;
  agregat: Record<string, StatDimension>;
  pyramide: number[];
  /** Niveaux forcés de la projection ; recalculés si absents. */
  niveauxForces?: Record<string, number>;
}): LectureExtremes {
  const { dimensions, vecteurCandidat, agregat, pyramide } = args;
  const kHaut = pyramide[0] ?? 1;
  const kBas = pyramide[pyramide.length - 1] ?? 1;
  const vEntrepriseComplet = vecteurEntreprise(args);

  // Restreint aux items présents des DEUX côtés : un item que le candidat n'a
  // pas classé n'est ni une convergence ni une friction, c'est une absence.
  const vEntreprise: VecteurQsort = {};
  const parCode = new Map<string, DimensionReferentiel>();
  for (const dimension of dimensions) {
    const stat = agregat[dimension.code];
    const scoreEntreprise = vEntrepriseComplet[dimension.code];
    if (stat === undefined || scoreEntreprise === undefined) continue;
    if (vecteurCandidat[dimension.code] === undefined) continue;
    vEntreprise[dimension.code] = scoreEntreprise;
    parCode.set(dimension.code, dimension);
  }
  const vCandidat: VecteurQsort = {};
  for (const code of parCode.keys()) vCandidat[code] = vecteurCandidat[code]!;

  const hautCandidat = pole(vCandidat, kHaut, "haut");
  const basCandidat = pole(vCandidat, kBas, "bas");
  const hautEntreprise = pole(vEntreprise, kHaut, "haut");
  const basEntreprise = pole(vEntreprise, kBas, "bas");

  const base = (code: string): ItemPole => {
    const dimension = parCode.get(code)!;
    return {
      code,
      label: dimension.label,
      categorie: dimension.categorie,
      scoreCandidat: vCandidat[code]!,
      scoreEntreprise: vEntreprise[code]!,
      dispersionEntreprise: agregat[code]!.ecartType,
    };
  };

  const convergences: Convergence[] = [];
  const frictions: Friction[] = [];
  for (const code of parCode.keys()) {
    const cHaut = hautCandidat.has(code);
    const cBas = basCandidat.has(code);
    const eHaut = hautEntreprise.has(code);
    const eBas = basEntreprise.has(code);
    // Un item peut appartenir aux deux pôles si la pyramide n'a qu'un niveau
    // utile ; l'ordre des tests fait alors primer la convergence haute.
    if (cHaut && eHaut) convergences.push({ ...base(code), pole: "haut" });
    else if (cBas && eBas) convergences.push({ ...base(code), pole: "bas" });
    else if (cHaut && eBas) frictions.push({ ...base(code), poleCandidat: "haut" });
    else if (cBas && eHaut) frictions.push({ ...base(code), poleCandidat: "bas" });
  }

  // Écart décroissant : la friction la plus large d'abord, la convergence la
  // plus nette d'abord (écart le plus faible → tri croissant sur |écart|).
  convergences.sort(
    (a, b) =>
      Math.abs(a.scoreCandidat - a.scoreEntreprise) -
      Math.abs(b.scoreCandidat - b.scoreEntreprise),
  );
  frictions.sort(
    (a, b) =>
      Math.abs(b.scoreCandidat - b.scoreEntreprise) -
      Math.abs(a.scoreCandidat - a.scoreEntreprise),
  );

  return { convergences, frictions, taillePole: { haut: kHaut, bas: kBas } };
}

// --------------------------------------------
// §3 — Détail par dimension
// --------------------------------------------

export type LigneDimension = {
  /** Clé de `categorie`, ou `null` pour les items sans catégorie. */
  cle: string | null;
  nbItems: number;
  /**
   * Position moyenne sur l'échelle du Q-sort — `nbNiveaux` = le plus valorisé,
   * 1 = le moins. Ce n'est PAS un rang (où 1 serait le meilleur) : le sens est
   * celui de tous les autres écrans, score haut = valorisé.
   */
  positionCandidat: number;
  positionEntreprise: number;
  /** Signé, candidat − entreprise. Positif = le candidat en attend davantage. */
  ecart: number;
  lecture: string;
};

/**
 * Jusqu'à ce seuil INCLUS, la dimension est déclarée alignée (notice : « un écart
 * faible (|écart| ≤ 1) = alignement »). Un cran de pyramide est l'unité de
 * mesure : en deçà, l'écart moyen d'une catégorie tient dans le bruit du tri.
 */
const SEUIL_ECART_DIMENSION = 1;
/** Au-delà, l'écart vaut plus d'un cran et demi de la pyramide. */
const SEUIL_ECART_MARQUE = 1.5;

function lectureDimension(
  ecart: number,
  positionCandidat: number,
  positionEntreprise: number,
  centre: number,
): string {
  if (Math.abs(ecart) <= SEUIL_ECART_DIMENSION) {
    if (positionCandidat > centre && positionEntreprise > centre) {
      return "Aligné, et fortement valorisé de part et d'autre — appui du matching.";
    }
    if (positionCandidat < centre && positionEntreprise < centre) {
      return "Aligné : registre secondaire pour les deux parties.";
    }
    return "Aligné, en position intermédiaire des deux côtés.";
  }
  const marque = Math.abs(ecart) >= SEUIL_ECART_MARQUE;
  if (ecart > 0) {
    return marque
      ? "Écart marqué : le candidat en attend nettement plus que ce que l'entreprise met en avant — vérifier ce qui est réellement offert."
      : "Le candidat en attend un peu plus que ce que l'entreprise met en avant.";
  }
  return marque
    ? "Écart marqué : l'entreprise met ce registre nettement plus en avant que le candidat ne le valorise — vérifier qu'il s'y retrouvera."
    : "L'entreprise met ce registre un peu plus en avant que le candidat ne le valorise.";
}

/**
 * Agrégation par `categorie` : moyenne des positions des items de la catégorie,
 * des deux côtés. Les vecteurs étant ipsatifs (même total), les moyennes sont
 * directement soustractibles sans normalisation.
 *
 * Ordre de PREMIÈRE APPARITION dans `dimensions`, pas par écart : le socle
 * d'items est ordonné par catégorie, ce qui donne un tableau à l'ordre stable
 * d'un candidat à l'autre — indispensable pour comparer deux rapports.
 */
export function detailParDimension(args: {
  dimensions: DimensionReferentiel[];
  vecteurCandidat: VecteurQsort;
  agregat: Record<string, StatDimension>;
  pyramide: number[];
  /** Niveaux forcés de la projection ; recalculés si absents. */
  niveauxForces?: Record<string, number>;
}): LigneDimension[] {
  const { dimensions, vecteurCandidat, pyramide } = args;
  const centre = (pyramide.length + 1) / 2;
  const vEntreprise = vecteurEntreprise(args);

  const ordre: (string | null)[] = [];
  const groupes = new Map<string | null, { cand: number[]; ent: number[] }>();
  for (const dimension of dimensions) {
    const scoreEntreprise = vEntreprise[dimension.code];
    const valeurCandidat = vecteurCandidat[dimension.code];
    if (scoreEntreprise === undefined || valeurCandidat === undefined) continue;
    const cle = dimension.categorie ?? null;
    let groupe = groupes.get(cle);
    if (!groupe) {
      groupe = { cand: [], ent: [] };
      groupes.set(cle, groupe);
      ordre.push(cle);
    }
    groupe.cand.push(valeurCandidat);
    groupe.ent.push(scoreEntreprise);
  }

  const moyenne = (v: number[]) => v.reduce((a, b) => a + b, 0) / v.length;
  return ordre.map((cle) => {
    const { cand, ent } = groupes.get(cle)!;
    const positionCandidat = moyenne(cand);
    const positionEntreprise = moyenne(ent);
    const ecart = positionCandidat - positionEntreprise;
    return {
      cle,
      nbItems: cand.length,
      positionCandidat,
      positionEntreprise,
      ecart,
      lecture: lectureDimension(
        ecart,
        positionCandidat,
        positionEntreprise,
        centre,
      ),
    };
  });
}

// --------------------------------------------
// §3bis — Matching item par item (radar)
// --------------------------------------------

/**
 * Un item du socle, confronté au niveau attendu par le référentiel. Forme
 * volontairement plate : c'est exactement ce que le radar de matching consomme.
 *
 * Les deux niveaux sont des SCORES DE PYRAMIDE (`scoreDuNiveau`) : `nbNiveaux`
 * = haut de pyramide (le plus caractéristique / le plus attendu), 1 = bas. Même
 * polarité que partout ailleurs, score haut = valorisé.
 */
export type LigneItemMatching = {
  code: string;
  label: string;
  categorie: string | null;
  /** Niveau attendu, entier : le reclassement forcé du référentiel. */
  niveauEntreprise: number;
  /** Niveau du candidat, entier : son propre placement dans la pyramide. */
  niveauCandidat: number;
  /** Signé, candidat − entreprise, en crans de pyramide. */
  ecart: number;
  /**
   * Statut au CRAN EXACT : aligné seulement si les deux niveaux coïncident.
   * Même règle que `comparerQsort`, qui ne tolère plus d'écart non plus — les
   * deux restitutions ne peuvent donc pas se contredire sur un même item.
   */
  statut: "dans-la-cible" | "sous-la-cible" | "au-dessus-de-la-cible";
};

/**
 * Matching item par item, dans l'ORDRE DU SOCLE (`dimensions`) : c'est l'ordre
 * qui découpe le cercle du radar, et il doit être le même d'un candidat à
 * l'autre pour que deux radars se superposent mentalement.
 *
 * Les items que l'agrégat ou la passation ne renseignent pas sont écartés : un
 * secteur sans niveau des deux côtés ne veut rien dire.
 */
export function matchingParItem(args: {
  dimensions: DimensionReferentiel[];
  vecteurCandidat: VecteurQsort;
  agregat: Record<string, StatDimension>;
  pyramide: number[];
  niveauxForces?: Record<string, number>;
}): LigneItemMatching[] {
  const vEntreprise = vecteurEntreprise(args);
  const lignes: LigneItemMatching[] = [];
  for (const dimension of args.dimensions) {
    const niveauEntreprise = vEntreprise[dimension.code];
    const niveauCandidat = args.vecteurCandidat[dimension.code];
    if (niveauEntreprise === undefined || niveauCandidat === undefined) continue;
    const ecart = niveauCandidat - niveauEntreprise;
    lignes.push({
      code: dimension.code,
      label: dimension.label,
      categorie: dimension.categorie ?? null,
      niveauEntreprise,
      niveauCandidat,
      ecart,
      statut:
        ecart === 0
          ? "dans-la-cible"
          : ecart < 0
            ? "sous-la-cible"
            : "au-dessus-de-la-cible",
    });
  }
  return lignes;
}

// --------------------------------------------
// §4 — Pistes d'entretien et avertissement
// --------------------------------------------

/**
 * Le fit culturel est une AIDE À LA DÉCISION. Repris tel quel dans toute
 * restitution — écran comme export — pour que le score ne circule jamais sans
 * son cadre d'usage.
 */
export const AVERTISSEMENT_RAPPORT =
  "Cet outil est une aide à la décision. Le score PC Fit ne constitue " +
  "pas à lui seul un motif de sélection ou de rejet, et doit être combiné aux " +
  "autres composantes du matching et à l'entretien. « Pas de tendance nette » " +
  "signale une absence de signal, non un fit médiocre.";

/** 0.5 → « 0,50 » : les seuils de la notice s'écrivent à la française. */
const virgule2 = (valeur: number) => valeur.toFixed(2).replace(".", ",");

/**
 * Notice d'interprétation du rapport (modèle métier « Rapport matching
 * culturel », onglet Notice). Reprise TELLE QUELLE dans toute restitution : les
 * chiffres du rapport ne se lisent pas sans ces sept règles, et notamment sans
 * « Pas de tendance nette » — une absence de signal, pas un demi-fit.
 *
 * Versionné avec le code plutôt qu'en base : c'est du texte de méthode, il suit
 * les seuils implémentés ici (`SEUIL_SIGNIFICATIVITE`, `SEUIL_VERDICT_NET`,
 * `SEUIL_ECART_DIMENSION`, `PYRAMIDE_FIT_CULTUREL`) et doit changer en même
 * temps qu'eux.
 */
export const NOTICE_INTERPRETATION: { titre: string; texte: string }[] = [
  {
    titre: "Ce que mesure le score",
    texte:
      "La proximité entre le classement des items par le CANDIDAT (ce qu'il " +
      "attend d'un poste) et celui de chaque RÉFÉRENT (ce qu'il faut pour " +
      "réussir dans ce poste et dans l'organisation) : la corrélation avec " +
      "chaque référent, puis leur moyenne. Elle varie de −1 (priorités " +
      "inversées) à +1 (accord). Plus les référents divergent entre eux, plus " +
      "elle est basse : un candidat qui pense comme un référent de plus obtient " +
      "à peu près l'accord moyen des référents entre eux.",
  },
  {
    titre: "Les verdicts",
    texte:
      "Proche ou Éloigné : la tendance se distingue du hasard (p ≤ .05) et " +
      `atteint ${virgule2(SEUIL_VERDICT_NET)} en valeur absolue. « À la ` +
      "limite » : elle se distingue du hasard sans atteindre " +
      `${virgule2(SEUIL_VERDICT_NET)} — à ${NB_ITEMS_FIT_CULTUREL} items, de ` +
      `${virgule2(rCritique(NB_ITEMS_FIT_CULTUREL))} à ` +
      `${virgule2(SEUIL_VERDICT_NET - 0.01)} en valeur absolue. Pas de tendance ` +
      "nette : elle ne se distingue pas du hasard. Ce n'est PAS un fit médiocre : les attentes du candidat " +
      "ne sont ni alignées ni inversées par rapport à celles des référents. " +
      "C'est une absence d'information — le PC Fit ne doit alors pas peser " +
      "dans la décision.",
  },
  {
    titre: "Pourquoi lire les extrêmes",
    texte:
      "Les items placés aux pôles du tri sont bien plus fiables que ceux du " +
      "centre (test-retest ~.87 aux extrêmes contre ~.61 au global dans la " +
      "littérature OCP). Les convergences et frictions extrêmes sont donc le " +
      "cœur interprétatif.",
  },
  {
    titre: "Convergence vs friction",
    texte:
      "Convergence = item valorisé par le candidat ET caractéristique de " +
      "l'entreprise. Friction = item à un extrême chez le candidat et à " +
      "l'extrême opposé chez l'entreprise. Une friction n'est pas " +
      "éliminatoire : c'est un point à explorer en entretien.",
  },
  {
    titre: "Détail par dimension",
    texte:
      "L'écart de rang moyen par dimension localise accords et tensions. Un " +
      "écart faible (|écart| ≤ 1) = alignement. Écart positif = le candidat " +
      "valorise davantage ; négatif = l'entreprise accentue davantage.",
  },
  {
    titre: "Cadre de décision",
    texte:
      "Aide à la décision uniquement. Combiner avec les autres composantes de " +
      "matching (CV, RH, Compétences, Personnalité & Motivations). Ne jamais " +
      "rejeter/retenir sur le seul score culturel.",
  },
  {
    titre: "Format du tri",
    texte:
      `Version réduite : ${NB_ITEMS_FIT_CULTUREL} items, ` +
      `${PYRAMIDE_FIT_CULTUREL.length} niveaux, distribution forcée ` +
      `${PYRAMIDE_FIT_CULTUREL.join("-")}. Même grille appliquée au profil de ` +
      "référence et au candidat, avec polarité identique.",
  },
];

/** Nombre de frictions nommées dans la piste d'entretien correspondante. */
const MAX_FRICTIONS_CITEES = 3;

/**
 * Pistes d'entretien dérivées des §2/§3. Les puces sont CONDITIONNELLES : une
 * piste « explorez vos frictions » sans friction détectée serait du remplissage,
 * et rendrait les autres puces moins lues.
 */
export function pistesEntretien(args: {
  indicateur: IndicateurGlobal;
  extremes: LectureExtremes;
  parDimension: LigneDimension[];
  fiabilite: "faible" | "correcte";
}): string[] {
  const pistes: string[] = [];

  const { frictions } = args.extremes;
  if (frictions.length > 0) {
    const cites = frictions
      .slice(0, MAX_FRICTIONS_CITEES)
      .map((f) => `« ${f.label} »`)
      .join(", ");
    const reste = frictions.length - MAX_FRICTIONS_CITEES;
    pistes.push(
      `Sur chaque friction (${cites}${reste > 0 ? ` et ${reste} autre${reste > 1 ? "s" : ""}` : ""}) : ` +
        "demander au candidat un exemple vécu, pour distinguer une vraie " +
        "divergence de valeurs d'un simple malentendu de formulation.",
    );
  }

  const ecarts = args.parDimension.filter(
    (l) => Math.abs(l.ecart) >= SEUIL_ECART_MARQUE,
  );
  if (ecarts.length > 0) {
    pistes.push(
      `Sur les dimensions au plus fort écart (${ecarts.map((l) => l.cle ?? "sans catégorie").join(", ")}) : ` +
        "vérifier que les attentes du candidat correspondent à ce que " +
        "l'entreprise offre réellement.",
    );
  } else {
    pistes.push(
      "Sur les dimensions critiques du poste : vérifier que les attentes du " +
        "candidat correspondent à ce que l'entreprise offre réellement.",
    );
  }

  // `sens` vaut « neutre » pour les deux cas : seul le libellé les distingue. Un
  // indicateur non calculable n'est pas une absence de tendance.
  if (args.indicateur.libelle === "Non calculable") {
    pistes.push(
      "Indicateur global non calculable : le PC Fit ne donne pas de verdict " +
        "d'ensemble pour ce candidat. Pondérer davantage les autres composantes " +
        "(compétences, personnalité, RH).",
    );
  } else if (args.indicateur.libelle === "Pas de tendance nette") {
    pistes.push(
      "Sans tendance nette, le PC Fit n'apporte pas d'information : pondérer " +
        "davantage les autres composantes (compétences, personnalité, RH).",
    );
  }

  if (args.fiabilite === "faible") {
    pistes.push(
      "Référentiel établi sur moins de 3 référents : traiter les convergences " +
        "et frictions comme des hypothèses d'entretien, pas comme des constats.",
    );
  }

  if (args.extremes.convergences.length > 0) {
    pistes.push(
      "Sur les convergences : faire préciser au candidat ce qu'il met derrière " +
        "l'item, pour confirmer que les deux parties parlent bien de la même chose.",
    );
  }

  return pistes;
}

// --------------------------------------------
// Assemblage
// --------------------------------------------

export type RapportMatchingCulturel = {
  indicateur: IndicateurGlobal;
  extremes: LectureExtremes;
  parDimension: LigneDimension[];
  /** Un item par secteur du radar de matching, dans l'ordre du socle. */
  parItem: LigneItemMatching[];
  pistes: string[];
  fiabilite: "faible" | "correcte";
  nbReferents: number;
  /** Hauteur de la pyramide — échelle de lecture des positions du §3. */
  nbNiveaux: number;
  avertissement: string;
  /** Notice d'interprétation : voyage AVEC le rapport, écran comme export. */
  notice: { titre: string; texte: string }[];
};

/**
 * Rapport de matching culturel complet pour UN candidat, contre UNE version du
 * référentiel. Pur : la version et la passation entrent, le rapport sort — rien
 * n'est stocké, exactement comme le score de matching (plan §8.1).
 */
export function construireRapportCulturel(args: {
  dimensions: DimensionReferentiel[];
  vecteurCandidat: VecteurQsort;
  agregat: Record<string, StatDimension>;
  pyramide: number[];
  nbReferents: number;
  /** Niveaux forcés portés par la projection ; recalculés si absents. */
  niveauxForces?: Record<string, number>;
}): RapportMatchingCulturel {
  const { dimensions, vecteurCandidat, agregat, pyramide, nbReferents } = args;
  const niveauxForces =
    args.niveauxForces ??
    niveauxForcesQsort({ dimensions, agregat, pyramide });

  // L'indicateur global compare le candidat à CHAQUE référent ; les pôles et
  // les écarts lisent la pyramide reclassée, celle qui est affichée — un seul
  // reclassement pour tout le rapport.
  const indicateur = interpreterCorrelation(
    correlationMoyenneReferents(vecteurCandidat, agregat, pyramide),
  );
  const extremes = lectureExtremes({
    dimensions,
    vecteurCandidat,
    agregat,
    pyramide,
    niveauxForces,
  });
  const parDimension = detailParDimension({
    dimensions,
    vecteurCandidat,
    agregat,
    pyramide,
    niveauxForces,
  });
  const parItem = matchingParItem({
    dimensions,
    vecteurCandidat,
    agregat,
    pyramide,
    niveauxForces,
  });
  const fiab = fiabilite(nbReferents);

  return {
    indicateur,
    extremes,
    parDimension,
    parItem,
    pistes: pistesEntretien({
      indicateur,
      extremes,
      parDimension,
      fiabilite: fiab,
    }),
    fiabilite: fiab,
    nbReferents,
    nbNiveaux: pyramide.length,
    avertissement: AVERTISSEMENT_RAPPORT,
    notice: NOTICE_INTERPRETATION,
  };
}
